/**
 * Party ledger: bills (+) and payments (−) in date order with a running balance.
 * mode "sales": invoices + PAYMENT_IN (balance = customer owes us)
 * mode "purchases": purchase bills + PAYMENT_OUT (balance = we owe the vendor)
 * Computed on the fly — nothing is stored.
 */
import { getUnrecordedPaidAmount } from "../../invoices/services/paymentService.js";

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const toDay = (value) => String(value || "").slice(0, 10);
const ORDER = { bill: 0, paidAtBilling: 1, payment: 2 };

/** Bills belonging to the party (by id, or by name for older records). */
export function filterPartyBills(bills = [], party, mode = "sales") {
  if (!party) return [];
  const name = String(party.name || "").toLowerCase();
  return bills.filter((b) => {
    const id = mode === "purchases" ? b.vendorId : b.customerId;
    const billName = String((mode === "purchases" ? b.vendorName : b.customerName) || "").toLowerCase();
    return (party.id && id === party.id) || (name && billName === name);
  });
}

function buildEntries({ bills, payments, mode }) {
  const entries = [];
  bills.forEach((bill) => {
    const date = toDay(mode === "purchases" ? bill.billDate : bill.invoiceDate) || toDay(bill.createdAt);
    const ref = (mode === "purchases" ? bill.vendorBillNo : bill.invoiceNo) || "—";
    entries.push({ kind: "bill", date, ref, amount: round2(bill.grandTotal), record: bill });
    const unrecorded = getUnrecordedPaidAmount(bill);
    if (unrecorded > 0) entries.push({ kind: "paidAtBilling", date, ref, amount: -unrecorded, record: bill });
  });
  payments
    .filter((p) => p.status !== "VOID" && p.type === (mode === "purchases" ? "PAYMENT_OUT" : "PAYMENT_IN"))
    .forEach((p) => {
      entries.push({
        kind: "payment",
        date: toDay(p.paymentDate) || toDay(p.createdAt),
        ref: p.paymentNo || "—",
        amount: -round2(p.amount),
        mode: p.paymentMode,
        record: p
      });
    });
  return entries.sort((a, b) =>
    a.date.localeCompare(b.date) || ORDER[a.kind] - ORDER[b.kind] || String(a.ref).localeCompare(String(b.ref), undefined, { numeric: true })
  );
}

/**
 * Returns { opening, rows, closing, billed, settled, allTimeBalance }.
 * rows: [{ kind, date, ref, amount, balance, record, mode }]; from/to are YYYY-MM-DD or "" (open-ended).
 * openingDue: dues from before the app (typed in on the party, per company) — counted before every entry.
 */
export function buildLedger({ bills = [], payments = [], from = "", to = "", mode = "sales", openingDue = 0 }) {
  const entries = buildEntries({ bills, payments, mode });
  let opening = round2(openingDue);
  let balance = opening;
  let billed = 0;
  let settled = 0;
  const rows = [];

  entries.forEach((e) => {
    if (from && e.date && e.date < from) {
      opening = round2(opening + e.amount);
      balance = opening;
      return;
    }
    if (to && e.date > to) return;
    balance = round2(balance + e.amount);
    if (e.amount > 0) billed = round2(billed + e.amount);
    else settled = round2(settled - e.amount);
    rows.push({ ...e, balance });
  });

  const allTimeBalance = round2(entries.reduce((sum, e) => sum + e.amount, round2(openingDue)));
  return { opening, rows, closing: rows.length ? balance : opening, billed, settled, allTimeBalance };
}
